import { useAppDispatch } from '@fiji/common/src/app/store';
import { setMessageContent } from '@fiji/common/src/features/common/commonSlice';
import { useTranslation } from 'react-i18next';
import { useNewCustomTranslations } from './useNewCustomTranslations';

/**
 * The `useImageValidated` function is a custom hook in TypeScript that returns a validator function
 * for checking if an image file is valid based on its format and size.
 * @returns The function `useImageValidated` returns an array containing a single element, which is the
 * `getValidator` function.
 */
export const useImageValidated = (): any[] => {
    useNewCustomTranslations('UPLOADING_IMAGE');
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    // const fileTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml'] as any;
    // const fileSize = 20000 as number;

    /**
     * The function `getValidator` checks if a file is a valid image format and if its size is less
     * than 2 MB.
     * @param {any} file - The `file` parameter is an object that represents an image file. It should
     * have properties like `type` (string) which represents the file type (e.g., 'image/png',
     * 'image/jpeg', etc.), and `size` (number) which represents the file size in bytes.
     * @returns The function `getValidator` returns a boolean value.
     */
    const getValidator = (file: any, fileType: any, fileSize: any): boolean => {
        if (!fileType?.includes(file?.type)) {
            dispatch(
                setMessageContent({
                    isOpen: true,
                    message: t('UPLOADING_IMAGE:INVALID_FORMAT'),
                    duration: 3000,
                })
            );
            return false;
        } else if (Math.floor(file?.size / 1024) > fileSize) {
            dispatch(
                setMessageContent({
                    isOpen: true,
                    message: t('UPLOADING_IMAGE:IMAGE_SIZE_WARN'),
                    duration: 3000,
                })
            );
            return false;
        }

        return true;
    };

    return [getValidator];
};
